import React, { useEffect, useState } from 'react';
import { Check, Loader2, Minus, ShieldCheck } from 'lucide-react';
import { apiService } from '../services/api';
import NexusMissingTools from './NexusMissingTools';

// Droits de Nexus (admins) : Nexus n'utilise un outil que si la personne ET le canal d'où il est convoqué y ont droit
// (et la clé d'accès). Chaque case : défaut (selon les droits de l'application) ou forcée (oui / non) par un admin.

const LABELS = {
  projets: 'Projets', ecriture: 'Écriture', documents: 'Documents', finances: 'Finances', pilotage: 'Pilotage',
  offres: 'Offres', personnel: 'Personnel', admin: 'Admin',
};

const Cell = ({ on, forced, locked, busy, onClick }) => (
  <button
    type="button"
    disabled={locked || busy}
    onClick={onClick}
    title={locked ? 'Un admin garde l’administration' : forced ? 'Réglé à la main (clic : suivant)' : 'Par défaut (clic : forcer)'}
    className={`relative inline-flex h-7 w-7 items-center justify-center rounded-md border transition
      ${on ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-300'}
      ${locked ? 'cursor-not-allowed opacity-60' : 'hover:border-gray-400'}`}
  >
    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : on ? <Check className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
    {forced && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-violet-500" />}
  </button>
);

const NexusRights = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => apiService.get('/nexus/rights').then(setData).catch((err) => setError(err.message));
  useEffect(() => { load(); }, []);

  // Défaut → forcé à l'inverse du défaut → retour au défaut.
  const cycle = async (person, domain) => {
    const isDefault = person.defaults.includes(domain);
    const forced = Object.prototype.hasOwnProperty.call(person.overrides, domain);
    const allowed = forced ? null : !isDefault;
    setBusy(`${person.employee_id}:${domain}`);
    try {
      await apiService.put(`/nexus/rights/${person.employee_id}`, { domain, allowed });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p className="p-6 text-sm text-red-600">{error}</p>;
  if (!data) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  const domains = Object.keys(data.domains);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <ShieldCheck className="h-5 w-5 text-blue-600" /> Droits de Nexus
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Nexus n'utilise un outil que si <strong>la personne</strong> et <strong>le canal</strong> d'où il est convoqué y
          ont droit (et la clé d'accès de la personne). Dans un channel Slack partagé, il ne voit que le projet du channel.
        </p>
      </header>

      <NexusMissingTools />

      <section className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <h2 className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">Canaux</h2>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-2 font-medium">Canal</th>
              {domains.map((d) => <th key={d} className="px-2 py-2 text-center font-medium" title={data.domains[d]}>{LABELS[d] || d}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {Object.entries(data.channels).map(([key, channel]) => (
              <tr key={key}>
                <td className="px-4 py-2 text-gray-800">
                  {channel.label}{channel.note && <span className="block text-xs text-gray-500">{channel.note}</span>}
                </td>
                {domains.map((d) => (
                  <td key={d} className="px-2 py-2 text-center">
                    {channel.domains.includes(d) ? <Check className="mx-auto h-4 w-4 text-emerald-600" /> : <Minus className="mx-auto h-4 w-4 text-gray-300" />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 px-4 py-2">
          <h2 className="text-sm font-semibold text-gray-700">Personnes</h2>
          <p className="text-xs text-gray-500">
            <span className="mr-1 inline-block h-2 w-2 rounded-full bg-violet-500" /> réglé à la main · sinon : droits par
            défaut (pilotage pour qui y a droit dans l'application, admin pour les admins)
          </p>
        </div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-2 font-medium">Personne</th>
              {domains.map((d) => <th key={d} className="px-2 py-2 text-center font-medium" title={data.domains[d]}>{LABELS[d] || d}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.people.map((person) => (
              <tr key={person.employee_id}>
                <td className="whitespace-nowrap px-4 py-1.5 text-gray-800">
                  {person.name}{person.is_admin && <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">admin</span>}
                </td>
                {domains.map((d) => (
                  <td key={d} className="px-2 py-1.5 text-center">
                    <Cell on={person.domains.includes(d)}
                      forced={Object.prototype.hasOwnProperty.call(person.overrides, d)}
                      locked={d === 'admin' && person.is_admin}
                      busy={busy === `${person.employee_id}:${d}`}
                      onClick={() => cycle(person, d)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <dl className="grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
        {domains.map((d) => (
          <div key={d}><dt className="inline font-semibold text-gray-800">{LABELS[d] || d} : </dt><dd className="inline">{data.domains[d]}</dd></div>
        ))}
      </dl>
    </div>
  );
};

export default NexusRights;
